import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { mockWS } from "./common";
import { EmscriptenModule } from "./emscripten";

declare global {
  interface Window {
    Module: EmscriptenModule;
  }
}

const baseWad = "freedm-quarry.wad";

const commonArgs = [
  "-iwad",
  baseWad,
  "-deathmatch",
  "-window",
  "-nogui",
  "-nomusic",
  "-config",
  "default.cfg",
  "-servername",
  "quarrydoom",
  "-nodes",
  "4",
];

export type DoomProps = {
  isHost?: boolean;
  petName: string;
  dup?: string;
  map?: string;
  extension?: string;
};

const defaultDup = 1;

// Defined in quarry-doom.js, used to interact with wasm
declare function callMain(args: string[]): void;

export const DoomCanvas = ({ petName, dup, map, extension, isHost }: DoomProps) => {
  const [loading, setLoading] = useState(true);
  const extensionPath = useMemo(() => (extension ? `${extension}.wad` : undefined), [extension]);
  const params = useMemo(() => {
    const dupParam = ["-dup", String(dup ?? defaultDup)];
    const wsParam = ["-wss", `wss://${mockWS}`];
    const mapParam = ["-warp", map ?? "07"];
    const extensionParam = extensionPath ? ["-file", extensionPath] : [];
    const petNameParam = ["-pet", petName];
    const serverParam = isHost ? ["-server", "-privateserver"] : ["-connect", "1"];
    return [...commonArgs, ...petNameParam, ...serverParam, ...extensionParam, ...dupParam, ...wsParam, ...mapParam];
  }, [petName, isHost, dup, extensionPath, map]);

  const start = useCallback(() => {
    callMain(params);
  }, [params]);

  const canvasRef = useRef<HTMLCanvasElement>(null);

  const hasWasm = useMemo(() => {
    try {
      if (typeof WebAssembly === "object" && typeof WebAssembly.instantiate === "function") {
        const module = new WebAssembly.Module(Uint8Array.of(0x0, 0x61, 0x73, 0x6d, 0x01, 0x00, 0x00, 0x00));
        if (module instanceof WebAssembly.Module)
          return new WebAssembly.Instance(module) instanceof WebAssembly.Instance;
      }
    } catch (e) {
      console.log(e);
    }
    return false;
  }, []);

  const loadWasm = useCallback(() => {
    const script = document.createElement("script");
    script.src = "/quarry-doom.js";
    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    window.Module = {
      ...(window.Module || {}),
      onRuntimeInitialized: () => {
        start();
      },
      noInitialRun: true,
      preRun: () => {
        window.Module.FS.createPreloadedFile("", "/" + baseWad, "/" + baseWad, true, true);
        if (extensionPath)
          window.Module.FS.createPreloadedFile("", "/" + extensionPath, "/" + extensionPath, true, true);
        window.Module.FS.createPreloadedFile("", "/default.cfg", "/default.cfg", true, true);
      },
      printErr: (text) => {
        console.error(text);
      },
      postRun: () => {},
      canvas: (() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        canvas.addEventListener(
          "webglcontextlost",
          function (e) {
            window.alert("WebGL context lost. You will need to reload the page.");
            e.preventDefault();
          },
          false,
        );
        return canvas;
      })(),
      print: (text) => {
        if (text.includes("connected")) {
          setLoading(false);
        }
        console.log("print:", text);
      },
      setStatus: (text) => {
        console.log("status:", text);
      },
      totalDependencies: 0,
      monitorRunDependencies: (left: number) => {
        window.Module.totalDependencies = Math.max(window.Module.totalDependencies, left);
        window.Module.setStatus(
          left
            ? "Preparing... (" + (window.Module.totalDependencies - left) + "/" + window.Module.totalDependencies + ")"
            : "All downloads complete.",
        );
      },
    };

    window.onerror = () => {
      window.Module.setStatus("Exception thrown, see JavaScript console");
      window.Module.setStatus = function (text) {
        if (text) window.Module.printErr("[post-exception status] " + text);
      };
    };

    loadWasm();
  }, []);

  // This is a hack to fix the scaling of the canvas when the loading is done or the window is resized
  // TODO: find a better solution
  useEffect(() => {
    if (!loading) {
      const canvas = canvasRef.current;
      if (canvas) {
        canvas.style.transform = `scale(1)`;
      }
    }
  }, [loading]);
  useEffect(() => {
    const handleResize = () => {
      const canvas = canvasRef.current;
      if (canvas) {
        const { innerWidth, innerHeight } = window;
        const scaleX = innerWidth / canvas.offsetWidth;
        const scaleY = innerHeight / canvas.offsetHeight;
        const scale = Math.min(scaleX, scaleY) * 0.9;
        canvas.style.transform = `scale(${scale})`;
      }
    };

    window.addEventListener("resize", handleResize);
    const timer = setInterval(() => {
      requestIdleCallback(handleResize, { timeout: 1000 });
    }, 1000);

    // Initial resize to set the canvas size
    handleResize();

    return () => {
      clearInterval(timer);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  if (!hasWasm) {
    return <div>Your browser has no WebAssembly Support. WeAssembly is required to run this game.</div>;
  }

  return (
    <canvas
      className={`bg-[url('/doom-title.png')] bg-no-repeat bg-center bg-contain absolute transition-all duration-100`}
      id="canvas"
      onContextMenu={(event) => event.preventDefault()}
      tabIndex={-1}
      ref={canvasRef}
    />
  );
};
